// AGC: useLotTimeline - Hook to fetch and combine notes and tasks for a lot
// AGC: Provides unified timeline data with real-time updates

import { useState, useEffect } from 'react'
import { collection, query, where, onSnapshot } from 'firebase/firestore'
import { db } from '../services/firebase-auth'
import { getTasks } from '../services/tasks'
import { useAuth } from './useAuth'
import type { LotNote, TimelineEntry, TimelineFilter } from '../types/lotNotes'
import type { Task } from '../types'

/**
 * AGC: useLotTimeline - Fetch and combine notes and tasks for a lot
 */
export const useLotTimeline = (
  lotId: string,
  filter: TimelineFilter = 'all'
) => {
  const [notes, setNotes] = useState<LotNote[]>([])
  const [tasks, setTasks] = useState<Task[]>([])
  const [timeline, setTimeline] = useState<TimelineEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // AGC: Get auth token for backend API calls
  const { getAuthToken } = useAuth()

  // AGC: Fetch notes in real-time
  useEffect(() => {
    if (!lotId) {
      setIsLoading(false)
      return
    }

    let unsubscribe: (() => void) | null = null

    try {
      // AGC: Query without orderBy to avoid composite index requirement
      // AGC: (array-contains + orderBy needs Firestore composite index). Sort in onSnapshot callback.
      const notesQuery = query(
        collection(db, 'lot_notes'),
        where('lotIds', 'array-contains', lotId)
      )

      unsubscribe = onSnapshot(
        notesQuery,
        (snapshot) => {
          const notesData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as LotNote))

          // AGC: Sort newest first in JS
          notesData.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

          setNotes(notesData)
          console.log(`📋 AGC: Loaded ${notesData.length} notes for lot ${lotId}`)
        },
        (err) => {
          console.error('AGC: Failed to fetch notes', err)
          setError('Failed to load notes')
        }
      )

      return () => {
        try {
          if (unsubscribe) {
            unsubscribe()
          }
        } catch (cleanupErr) {
          // AGC: Silently ignore Firestore cleanup errors (SDK bug)
          console.warn('AGC: Firestore cleanup warning (harmless):', cleanupErr)
        }
      }
    } catch (err) {
      console.error('AGC: Error setting up notes listener', err)
      setError('Failed to setup notes listener')
    }
  }, [lotId])

  // AGC: Fetch tasks from backend API (same as LotPage)
  useEffect(() => {
    if (!lotId) {
      setIsLoading(false)
      return
    }

    const fetchTasks = async () => {
      try {
        const token = await getAuthToken()
        const allTasks = await getTasks(token)
        
        // AGC: Filter tasks by lotId (same logic as LotPage)
        const lotTasks = allTasks.filter(task => task.plotId === lotId)
        
        setTasks(lotTasks)
        console.log(`📋 AGC: Loaded ${lotTasks.length} tasks for lot ${lotId} (from ${allTasks.length} total)`)
        setIsLoading(false)
      } catch (err) {
        console.error('AGC: Failed to fetch tasks', err)
        setError('Failed to load tasks')
        setIsLoading(false)
      }
    }

    fetchTasks()
  }, [lotId, getAuthToken])

  // AGC: Combine and filter timeline entries
  useEffect(() => {
    const entries: TimelineEntry[] = []

    // AGC: Add notes if filter allows
    if (filter === 'all' || filter === 'notes') {
      const noteEntries: TimelineEntry[] = notes.map(note => ({
        id: note.id,
        type: 'note',
        timestamp: note.createdAt,
        title: getNoteTitleFromType(note.type),
        description: note.content,
        icon: getNoteIcon(note.type),
        lotIds: note.lotIds,
        userId: note.userId,
        userName: note.userName,
        noteData: note,
      }))
      
      entries.push(...noteEntries)
    }

    // AGC: Add tasks if filter allows
    if (filter === 'all' || filter === 'tasks') {
      const taskEntries: TimelineEntry[] = tasks.map(task => ({
        id: task.id,
        type: 'task',
        timestamp: task.createdAt,
        title: task.title,
        description: task.description,
        icon: getTaskIcon(task.category),
        lotIds: [task.plotId],
        userId: task.createdBy,
        taskData: task,
      }))
      
      entries.push(...taskEntries)
    }

    // AGC: Sort by timestamp (newest first)
    entries.sort((a, b) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    )

    setTimeline(entries)
  }, [notes, tasks, filter])

  /**
   * AGC: Get note title from type
   */
  const getNoteTitleFromType = (type: string): string => {
    switch (type) {
      case 'photo': return 'Photo'
      case 'video': return 'Video'
      case 'audio': return 'Audio Note'
      case 'text': return 'Text Note'
      default: return 'Note'
    }
  }

  /**
   * AGC: Get note icon
   */
  const getNoteIcon = (type: string): string => {
    switch (type) {
      case 'photo': return '📷'
      case 'video': return '🎬'
      case 'audio': return '🎤'
      case 'text': return '📝'
      default: return '📋'
    }
  }

  /**
   * AGC: Get task icon
   */
  const getTaskIcon = (category?: string): string => {
    switch (category) {
      case 'planting': return '🌱'
      case 'irrigation': return '💧'
      case 'fertilization': return '🧪'
      case 'spraying': return '💨'
      case 'harvesting': return '🌾'
      case 'maintenance': return '🔧'
      default: return '✅'
    }
  }

  return {
    notes,
    tasks,
    timeline,
    isLoading,
    error,
    noteCount: notes.length,
    taskCount: tasks.length,
    totalCount: timeline.length,
  }
}

export default useLotTimeline
